/**
 * <collection-embed> — Embedded Collection feed for aeyde PLPs.
 *
 * Renders ANOTHER collection's products at the bottom of a collection page via
 * infinite scroll. Mirrors the host infinite-scroll mechanism: fetches the target
 * collection's real rendered pages, extracts its product grid, and appends the
 * cards into its own grid — so the cards match the theme's product card exactly.
 *
 * Cards self-initialize on hover via the global `window.handleCardHover` (already
 * defined by the host page's cards) and lazysizes auto-loads their images.
 *
 * Hides itself while a filter is active on the host collection (browse-only).
 *
 * Markup:
 *   <collection-embed data-target-url="/collections/jewelry" data-source="footwear"
 *                     data-max-pages="0" data-load-trigger-vh="100">
 *     <div class="product__grid_4" data-embed-grid></div>
 *     <div data-embed-loader>…spinner…</div>
 *   </collection-embed>
 */
(function () {
  class CollectionEmbed extends HTMLElement {
    constructor() {
      super();
      this.loadingPage = false;
      this.currentPage = 1;
      this.totalPages = null;
      this.scriptsInited = false;
      this.fetchedUrls = new Set();
      this.hostWatcher = null;
      this.hostWaitFallback = null;
    }

    connectedCallback() {
      this.targetUrl = this.dataset.targetUrl || '';
      this.sourceHandle = this.dataset.source || '';
      this.maxPages = parseInt(this.dataset.maxPages, 10) || 0; // 0 = all pages
      this.loadTriggerVh = parseInt(this.dataset.loadTriggerVh, 10) || 100;
      this.gridEl = this.querySelector('[data-embed-grid]');
      this.loaderEl = this.querySelector('[data-embed-loader]');

      if (!this.targetUrl || !this.gridEl || !this.loaderEl) return;

      this.watchFilters();
      this.setupObserver();
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
      this.filterObserver && this.filterObserver.disconnect();
      this.clearHostWait();
    }

    /**
     * Hide the embed while a filter is active on the host collection.
     * The filter sets `data-filters-active` on the matching .plp-header.
     */
    watchFilters() {
      const header =
        (this.sourceHandle &&
          document.querySelector('.plp-header[data-collection="' + this.sourceHandle + '"]')) ||
        document.querySelector('.plp-header[data-collection]');

      const apply = () => {
        this.hidden = !!document.querySelector('.plp-header[data-filters-active]');
      };

      apply();

      if (header) {
        this.filterObserver = new MutationObserver(apply);
        this.filterObserver.observe(header, {
          attributes: true,
          attributeFilter: ['data-filters-active'],
        });
      }
    }

    setupObserver() {
      // IntersectionObserver only supports px/% in rootMargin — convert vh to px.
      const triggerPx = Math.round((this.loadTriggerVh / 100) * window.innerHeight);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.loadingPage) {
            this.loadNextPage();
          }
        },
        { root: null, rootMargin: '0px 0px ' + triggerPx + 'px 0px', threshold: 0 }
      );

      this.observer.observe(this.loaderEl);
    }

    /**
     * Fetch the next target page, extract its product grid, and append the cards.
     */
    async loadNextPage() {
      if (this.loadingPage) return;

      // The embed must appear AFTER the full host collection: wait until the host's
      // own infinite scroll has loaded every page before loading any target product.
      if (!this.hostIsDone()) {
        this.waitForHost();
        return;
      }

      if (this.maxPages > 0 && this.currentPage - 1 >= this.maxPages) return this.stop();
      if (this.totalPages !== null && this.currentPage > this.totalPages) return this.stop();

      const url = new URL(this.targetUrl, window.location.origin);
      url.searchParams.set('page', this.currentPage);
      const href = url.href;

      if (this.fetchedUrls.has(href)) return this.stop();

      this.loadingPage = true;
      this.fetchedUrls.add(href);

      try {
        const response = await fetch(href, { headers: { 'Content-Type': 'text/html' } });
        if (!response.ok) throw new Error('Failed to fetch ' + href + ': ' + response.status);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        // Total pages come from the target's own infinite-scroll element (first fetch).
        if (this.totalPages === null) {
          const is = doc.querySelector('infinite-scroll[data-page-size]');
          this.totalPages = is ? parseInt(is.dataset.pageSize, 10) || 1 : 1;
        }

        const sourceGrid =
          doc.querySelector('.product__grid_4.unique') ||
          doc.querySelector('.product__grid_4') ||
          doc.querySelector('.product__grid');

        if (sourceGrid) {
          // Drop the target's infinite-scroll element — we drive pagination ourselves.
          sourceGrid.querySelectorAll('infinite-scroll').forEach((el) => el.remove());
          this.gridEl.insertAdjacentHTML('beforeend', sourceGrid.innerHTML);
          this.initCardScripts();
        }

        this.currentPage++;

        const capReached = this.maxPages > 0 && this.currentPage - 1 >= this.maxPages;
        if (capReached || this.currentPage > this.totalPages) {
          this.stop();
        } else {
          // IO only fires on transitions — cycle to re-check if the loader stays in view.
          this.observer.unobserve(this.loaderEl);
          this.observer.observe(this.loaderEl);
        }
      } catch (error) {
        console.error('CollectionEmbed:', error);
      } finally {
        this.loadingPage = false;
      }
    }

    /**
     * Whether the host collection's infinite scroll has loaded ALL its pages.
     * True when there is no host infinite-scroll, or the highest page already
     * appended to the host grid has reached the host's total page count.
     */
    hostIsDone() {
      const hostGrid = document.querySelector('.product__grid_4.unique');
      if (!hostGrid) return true;

      const host = hostGrid.querySelector('infinite-scroll');
      if (!host) return true;

      const pageSize = parseInt(host.dataset.pageSize, 10) || 1;
      if (pageSize <= 1) return true;

      let maxPage = 1;
      hostGrid.querySelectorAll('[data-page]').forEach((el) => {
        const page = parseInt(el.getAttribute('data-page'), 10);
        if (page > maxPage) maxPage = page;
      });

      return maxPage >= pageSize;
    }

    /**
     * Watch the host grid and resume loading once the host finishes appending all
     * its pages. A fallback timer guards against the host stalling (e.g. failed fetch).
     */
    waitForHost() {
      if (this.hostWatcher) return;

      const hostGrid = document.querySelector('.product__grid_4.unique');
      if (!hostGrid) return;

      this.hostWatcher = new MutationObserver(() => {
        if (this.hostIsDone()) {
          this.clearHostWait();
          if (!this.loadingPage) this.loadNextPage();
        }
      });
      this.hostWatcher.observe(hostGrid, { childList: true, subtree: true });

      this.hostWaitFallback = setTimeout(() => {
        this.clearHostWait();
        if (!this.loadingPage) this.loadNextPage();
      }, 10000);
    }

    clearHostWait() {
      if (this.hostWatcher) {
        this.hostWatcher.disconnect();
        this.hostWatcher = null;
      }
      if (this.hostWaitFallback) {
        clearTimeout(this.hostWaitFallback);
        this.hostWaitFallback = null;
      }
    }

    /**
     * Product cards carry an inline `<script data-product-card-script>` that defines
     * window.handleCardHover (+ quick-add). It's already defined by the host page, but
     * run it once defensively, then strip the inert duplicates injected via innerHTML.
     */
    initCardScripts() {
      if (!this.scriptsInited) {
        for (const name of ['data-product-card-script', 'data-quick-add-script']) {
          const original = this.gridEl.querySelector('script[' + name + ']');
          if (original) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.textContent = original.textContent;
            document.body.appendChild(script);
          }
        }
        this.scriptsInited = true;
      }

      this.gridEl
        .querySelectorAll('script[data-product-card-script], script[data-quick-add-script]')
        .forEach((script) => script.remove());
    }

    stop() {
      this.observer && this.observer.disconnect();
      if (this.loaderEl) this.loaderEl.style.display = 'none';
    }
  }

  if (!customElements.get('collection-embed')) {
    customElements.define('collection-embed', CollectionEmbed);
  }
})();
